import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import type { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { COMMON } from "@redotech/redo-team-common/localization/language";
import { useContext, useMemo } from "react";
import { SettingsContext } from "../contexts/settings";
import { CustomizationContext } from "./customization-context";
import { resolveCustomizationField } from "./resolve-customization-field";

// Slots on the portal-builder confirmation page mapped to the legacy
// resourceOverride key each one drives. Each slot has a "return" branch
// (text/draftOrder/steps) and a parallel "warranty" branch
// (warrantyText/warrantyDraftOrder/warrantySteps).
//
// On every render we fold:
//   - the merchant's "text/draftOrder/steps" edits into
//     resourceOverride.COMMON.<key> — applies to non-warranty variants and
//     to warranty when warrantyText is blank for a field (but see below).
//   - the merchant's warranty edits (or the warranty hardcoded default when
//     blank) into resourceOverride.COMMON.variants.warranty.<key> — wins
//     over the COMMON override when copyResource is resolved with
//     variant="warranty".
//
// Unconditionally re-asserting the warranty default into the variant override
// when warrantyText is blank is what kills the cascade: a return-flow edit
// to `text.heading` never bleeds onto the warranty confirmation page,
// because the warranty layer always provides an explicit value that
// outranks the COMMON layer in getResource(). copyResource resolves
// variant from `returnData.type`, so we don't need to know the variant
// up front and don't depend on the route mount being correct.
const CONFIRMATION_LEGACY_KEY_MAP = {
  heading: "CONFIRMATION_HEADER_TEXT_SUBMIT",
  subtitle: "CONFIRMATION_HEADER_SUBTEXT",
  draftOrderHeading: "DRAFT_ORDER_HEADER",
  draftOrderSubtitle: "DRAFT_ORDER_SUBTITLE",
  stepsTitle: "STATUS_STEPS_TITLE",
  manualReviewTitle: "STATUS_MANUAL_REVIEW_TITLE",
  manualReviewSubtitle: "STATUS_MANUAL_REVIEW_SUBTITLE",
  inTransitTitle: "STATUS_IN_TRANSIT_TITLE",
  inTransitSubtitle: "STATUS_IN_TRANSIT_SUBTITLE",
  processedTitle: "STATUS_PROCESSED_TITLE",
  processedSubtitle: "STATUS_PROCESSED_SUBTITLE",
  processedRepairTitle: "STATUS_PROCESSED_REPAIR_TITLE",
  processedRepairSubtitle: "STATUS_PROCESSED_REPAIR_SUBTITLE",
  dropOffPackageTitle: "DROP_OFF_PACKAGE_TEXT",
  dropOffPackageSubtitle: "DROP_OFF_PACKAGE_SUBTEXT",
  greenReturnProcessedTitle: "GREEN_RETURN_PROCESS_STEP_TEXT",
} as const;

type ConfirmationSlotKey = keyof typeof CONFIRMATION_LEGACY_KEY_MAP;
type ConfirmationLegacyKey =
  (typeof CONFIRMATION_LEGACY_KEY_MAP)[ConfirmationSlotKey];

type ConfirmationPage =
  | NonNullable<NonNullable<PortalCustomization["pages"]>["confirmation"]>
  | null
  | undefined;

function pickReturnSlot(
  page: ConfirmationPage,
  slot: ConfirmationSlotKey,
): string | null | undefined {
  if (!page) {
    return undefined;
  }
  switch (slot) {
    case "heading":
      return page.text?.heading;
    case "subtitle":
      return page.text?.subtitle;
    case "draftOrderHeading":
      return page.draftOrder?.heading;
    case "draftOrderSubtitle":
      return page.draftOrder?.subtitle;
    case "stepsTitle":
      return page.steps?.title;
    case "manualReviewTitle":
      return page.steps?.manualReview?.title;
    case "manualReviewSubtitle":
      return page.steps?.manualReview?.subtitle;
    case "inTransitTitle":
      return page.steps?.inTransit?.title;
    case "inTransitSubtitle":
      return page.steps?.inTransit?.subtitle;
    case "processedTitle":
      return page.steps?.processed?.title;
    case "processedSubtitle":
      return page.steps?.processed?.subtitle;
    case "processedRepairTitle":
      return page.steps?.processedRepair?.title;
    case "processedRepairSubtitle":
      return page.steps?.processedRepair?.subtitle;
    case "dropOffPackageTitle":
      return page.steps?.dropOffPackage?.title;
    case "dropOffPackageSubtitle":
      return page.steps?.dropOffPackage?.subtitle;
    case "greenReturnProcessedTitle":
      return page.steps?.greenReturnProcessed?.title;
  }
}

function pickWarrantySlot(
  page: ConfirmationPage,
  slot: ConfirmationSlotKey,
): string | null | undefined {
  if (!page) {
    return undefined;
  }
  switch (slot) {
    case "heading":
      return page.warrantyText?.heading;
    case "subtitle":
      return page.warrantyText?.subtitle;
    case "draftOrderHeading":
      return page.warrantyDraftOrder?.heading;
    case "draftOrderSubtitle":
      return page.warrantyDraftOrder?.subtitle;
    case "stepsTitle":
      return page.warrantySteps?.title;
    case "manualReviewTitle":
      return page.warrantySteps?.manualReview?.title;
    case "manualReviewSubtitle":
      return page.warrantySteps?.manualReview?.subtitle;
    case "inTransitTitle":
      return page.warrantySteps?.inTransit?.title;
    case "inTransitSubtitle":
      return page.warrantySteps?.inTransit?.subtitle;
    case "processedTitle":
      return page.warrantySteps?.processed?.title;
    case "processedSubtitle":
      return page.warrantySteps?.processed?.subtitle;
    case "processedRepairTitle":
      return page.warrantySteps?.processedRepair?.title;
    case "processedRepairSubtitle":
      return page.warrantySteps?.processedRepair?.subtitle;
    case "dropOffPackageTitle":
      return page.warrantySteps?.dropOffPackage?.title;
    case "dropOffPackageSubtitle":
      return page.warrantySteps?.dropOffPackage?.subtitle;
    case "greenReturnProcessedTitle":
      return page.warrantySteps?.greenReturnProcessed?.title;
  }
}

/** Warranty hardcoded default for a legacy key — variants.warranty value if
 * it exists, else the COMMON default. Used to fill the warranty layer
 * unconditionally so the cascade never reintroduces the return-flow text. */
function warrantyDefault(key: ConfirmationLegacyKey): string {
  return COMMON.variants?.warranty?.[key] ?? COMMON[key];
}

export function resolveConfirmationSettings(
  settings: CustomerPortalAppSettings | undefined,
  customization: PortalCustomization | null,
): CustomerPortalAppSettings | undefined {
  if (!settings) {
    return settings;
  }
  const page = customization?.pages?.confirmation;
  if (!page) {
    return settings;
  }

  type CommonOverride = NonNullable<
    NonNullable<CustomerPortalAppSettings["resourceOverride"]>["COMMON"]
  >;
  const legacyCommon: CommonOverride = settings.resourceOverride?.COMMON ?? {};
  const mergedCommon: CommonOverride = { ...legacyCommon };

  // Existing warranty variant overrides (rare but possible). We compose on
  // top of them so we don't blow away anything that wasn't ours.
  const existingWarranty = legacyCommon.variants?.warranty ?? {};
  const mergedWarranty: Partial<CommonOverride> = { ...existingWarranty };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  for (const slot of Object.keys(
    CONFIRMATION_LEGACY_KEY_MAP,
  ) as ConfirmationSlotKey[]) {
    const legacyKey = CONFIRMATION_LEGACY_KEY_MAP[slot];

    const returnSlot = pickReturnSlot(page, slot);
    if (returnSlot) {
      mergedCommon[legacyKey] = resolveCustomizationField(
        returnSlot,
        legacyCommon[legacyKey],
      );
    }

    const warrantySlot = pickWarrantySlot(page, slot);
    // Unconditionally assert a value into the warranty variant layer:
    //   - merchant warranty edit if set,
    //   - existing warranty override if previously set,
    //   - the warranty hardcoded default otherwise.
    // This is what kills the cascade — the warranty layer always wins
    // over COMMON during getResource(variant="warranty"), so a return-
    // flow `text` edit can never bleed onto a warranty confirmation.
    mergedWarranty[legacyKey] = warrantySlot
      ? resolveCustomizationField(
          warrantySlot,
          existingWarranty[legacyKey] ?? warrantyDefault(legacyKey),
        )
      : (existingWarranty[legacyKey] ?? warrantyDefault(legacyKey));
  }

  mergedCommon.variants = {
    ...legacyCommon.variants,
    warranty: mergedWarranty,
  };

  return {
    ...settings,
    resourceOverride: { ...settings.resourceOverride, COMMON: mergedCommon },
  };
}

/** Returns the portal app settings with the portal-builder confirmation-page
 * customizations layered into `resourceOverride`. Pass the result to
 * `useReturnConfirmation` so that hook and its nested `useSteps` pick up
 * the merchant's latest edits via their existing `copyResource` read path.
 *
 * The merge folds both branches at once:
 *   - return-flow edits → resourceOverride.COMMON.<key>
 *   - warranty edits (or warranty hardcoded default if blank) →
 *     resourceOverride.COMMON.variants.warranty.<key>
 *
 * No variant argument is required — `getResource()` inside
 * `useReturnConfirmation` reads the actual `returnData.type` and selects
 * the right layer, so this is robust to any deep link or routing path. */
export function useConfirmationCustomization():
  | CustomerPortalAppSettings
  | undefined {
  const settings = useContext(SettingsContext);
  const { customization } = useContext(CustomizationContext);

  return useMemo(
    () => resolveConfirmationSettings(settings, customization),
    [customization, settings],
  );
}
